import { useProgress } from '../store/progress.js';
import { Icons } from './Icons.jsx';
import CroFlag from './CroFlag.jsx';
import BottomTabs from './BottomTabs.jsx';
import { BALL } from '../theme.js';

const T = BALL;

function StatTile({ icon, value, label, iconColor }) {
  return (
    <div style={{
      background: T.bgAlt, border: `2px solid ${T.hairline}`,
      borderRadius: 14, padding: '12px 12px 10px',
      boxShadow: `0 3px 0 ${T.hairline}`,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: '#FFF1E6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: iconColor,
      }}>{icon}</div>
      <div>
        <div style={{
          fontSize: 18, fontWeight: 900, color: T.ink, lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}>{value}</div>
        <div style={{
          fontSize: 11, fontWeight: 700, color: T.inkSoft, marginTop: 3,
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>{label}</div>
      </div>
    </div>
  );
}

function Achievement({ tier, title, sub, locked, color, progress }) {
  return (
    <div style={{
      background: T.bgAlt, border: `2px solid ${T.hairline}`,
      borderRadius: 14, padding: 12,
      boxShadow: `0 3px 0 ${T.hairline}`,
      display: 'flex', alignItems: 'center', gap: 12,
      opacity: locked ? 0.55 : 1,
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: 12,
        background: locked ? T.locked : color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: locked ? 'none' : `0 3px 0 ${T.primaryDeep}`,
      }}>
        {tier === 'flame'  && Icons.flame('#fff')}
        {tier === 'star'   && Icons.star('#fff')}
        {tier === 'trophy' && Icons.trophy()}
        {tier === 'gem'    && Icons.gem('#fff')}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 900, color: T.ink, fontSize: 14 }}>{title}</div>
        <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 2 }}>{sub}</div>
        {!locked && progress > 0 && (
          <div style={{
            marginTop: 6, height: 6, background: T.hairline,
            borderRadius: 99, overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.min(100, progress * 100)}%`, height: '100%',
              background: color, borderRadius: 99,
            }} />
          </div>
        )}
      </div>
    </div>
  );
}

function thisWeekStrip(progress, lang) {
  // Build M-T-W-T-F-S-S cells based on streak.
  // We don't track historical per-day completion, so we approximate by
  // marking the most recent `min(streak, 7)` weekdays as done.
  const today = new Date();
  const dayIdx = (today.getDay() + 6) % 7; // 0 = Monday
  const cells = [];
  const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  for (let i = 0; i < 7; i++) {
    const offset = dayIdx - i;
    let state = 'locked';
    if (offset === 0) state = progress.todayXP > 0 ? 'done' : 'today';
    else if (offset > 0 && offset <= progress.streak - (progress.todayXP > 0 ? 1 : 0)) state = 'done';
    cells.push({ label: dayLabels[i], state });
  }

  return (
    <div style={{
      background: T.bgAlt, border: `2px solid ${T.hairline}`,
      borderRadius: 14, padding: '14px 16px',
      boxShadow: `0 3px 0 ${T.hairline}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10,
      }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: T.ink }}>
          {lang === 'en' ? 'This week' : 'Den här veckan'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {Icons.flame()}
          <span style={{
            fontWeight: 900, color: T.primary,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {progress.streak} {lang === 'en' ? 'day streak' : 'dagars streak'}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
        {cells.map((c, i) => (
          <div key={i} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1,
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.inkSoft }}>{c.label}</div>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: c.state === 'done' ? T.primary : c.state === 'today' ? '#FFF1E6' : T.locked,
              border: c.state === 'today' ? `2.5px dashed ${T.primary}` : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {c.state === 'done' && (
                <span style={{ color: '#fff', fontWeight: 900, fontSize: 16 }}>✓</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Profile({ onOpenSettings, onTabChange }) {
  const progress = useProgress();
  const lang = progress.sourceLang;

  // Compute "words learned" — items where SRS strength >= 3.
  const wordsLearned = Object.values(progress.srs).filter((s) => s.strength >= 3).length;

  // Achievement progress
  const streakAch = Math.min(1, progress.streak / 14);
  const xpAch = Math.min(1, progress.xp / 3000);

  return (
    <div className="app-shell">
      {/* Header */}
      <div style={{
        padding: 'calc(env(safe-area-inset-top) + 12px) 20px 16px',
        background: T.bgAlt, borderBottom: `1px solid ${T.hairline}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: T.primary, border: `3px solid ${T.primaryDeep}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 900, fontSize: 28,
          }}>
            {/* User initials placeholder — could read from settings later. */}
            🐶
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: T.ink }}>
              {lang === 'en' ? 'Croatian Learner' : 'Kroatiska-lärling'}
            </div>
            <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 2 }}>
              {lang === 'en' ? 'Joined' : 'Med sedan'} {new Date().toLocaleDateString(lang === 'en' ? 'en-US' : 'sv-SE', { month: 'long', year: 'numeric' })}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
              <CroFlag size={22} />
              <span style={{ fontSize: 12, fontWeight: 700, color: T.inkSoft }}>
                {lang === 'en' ? 'Learning Croatian' : 'Lär sig kroatiska'}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onOpenSettings}
            aria-label="settings"
            style={{
              appearance: 'none', border: 'none', background: 'none', cursor: 'pointer',
              padding: 6,
            }}
          >
            {Icons.cog(T.inkSoft)}
          </button>
        </div>
      </div>

      <div className="app-scroll">
        {/* Stats */}
        <div style={{ padding: '16px 16px 6px' }}>
          <div style={{
            fontSize: 13, fontWeight: 900, color: T.ink,
            letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10,
          }}>
            {lang === 'en' ? 'Statistics' : 'Statistik'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <StatTile icon={Icons.flame()} value={progress.streak} iconColor={T.primary}
              label={lang === 'en' ? 'Day streak' : 'Dagars streak'} />
            <StatTile icon={Icons.star()} value={progress.xp.toLocaleString(lang === 'en' ? 'en-US' : 'sv-SE')}
              iconColor={T.gold}
              label={lang === 'en' ? 'Total XP' : 'Total XP'} />
            <StatTile icon={Icons.gem()} value={progress.gems} iconColor={T.blue}
              label={lang === 'en' ? 'Gems' : 'Ädelstenar'} />
            <StatTile icon={Icons.book()} value={wordsLearned} iconColor={T.mint}
              label={lang === 'en' ? 'Words learned' : 'Ord lärda'} />
          </div>
        </div>

        {/* Week strip */}
        <div style={{ padding: '14px 16px 8px' }}>
          {thisWeekStrip(progress, lang)}
        </div>

        {/* Achievements */}
        <div style={{ padding: '10px 16px 24px' }}>
          <div style={{
            fontSize: 13, fontWeight: 900, color: T.ink,
            letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10,
          }}>
            {lang === 'en' ? 'Achievements' : 'Bedrifter'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Achievement
              tier="flame"
              title={lang === 'en' ? 'Wildfire' : 'Eldsjäl'}
              sub={lang === 'en' ? `Reach a 14 day streak (${progress.streak}/14)`
                                  : `Nå en 14-dagars streak (${progress.streak}/14)`}
              color={T.primary}
              progress={streakAch}
            />
            <Achievement
              tier="star"
              title={lang === 'en' ? 'Sage' : 'Visdom'}
              sub={lang === 'en' ? `Earn 3,000 XP (${progress.xp}/3000)`
                                  : `Tjäna 3 000 XP (${progress.xp}/3000)`}
              color={T.gold}
              progress={xpAch}
            />
            <Achievement
              tier="gem"
              title={lang === 'en' ? 'Scholar' : 'Lärd'}
              sub={lang === 'en' ? `Learn 250 new words (${wordsLearned}/250)`
                                  : `Lär dig 250 nya ord (${wordsLearned}/250)`}
              locked={wordsLearned < 5}
              color={T.blue}
              progress={Math.min(1, wordsLearned / 250)}
            />
          </div>
        </div>
      </div>

      <BottomTabs active="profile" onSelect={onTabChange} theme={T} />
    </div>
  );
}
